import React, { useCallback, useEffect } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { isNil } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import ERROR_MESSAGES from 'types/errorMessages';
import PageWrapper from 'modules/layout/pageWrapper.component';
import SportScaleTable from 'common/table/sportScaleTable.component';
import SportScaleTableActionButton from 'common/table/sportScaleTableActionButton.component';
import SportScaleTableToolbar from 'common/table/sportScaleTableToolbar.component';
import useApiRequest from 'hooks/apiRequest';
import {
  GET_ORGANIZATION_LOCATIONS_SUCCESS,
  getOrganizationLocations,
  resetOrganizationLocations,
} from 'modules/locations/locations.actions';
import { NEW_ITEM_ROUTE } from 'types/routes';
import { PAGE_TITLES } from 'modules/layout/drawer/drawerItems';
import { PERMISSIONS, can } from 'common/permissions';
import { caseInsensitiveSort } from 'utilities/sorting';
import { selectLocations, selectLocationsIsLoadingLocations } from 'modules/locations/locations.selectors';
import {
  selectSelectedOrganizationId,
  selectSelectedOrganizationName,
} from 'modules/organizations/organizations.selectors';
import { setPageTitle } from 'modules/layout/layout.actions';

const COLUMNS = [
  { title: 'Name', field: 'name', customSort: caseInsensitiveSort('name') },
  {
    title: 'Sportscale Address',
    field: 'type',
  },
];

const LocationsContainer = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const makeRequest = useApiRequest();

  const isLoadingLocations = useSelector(selectLocationsIsLoadingLocations);
  const locations = useSelector(selectLocations);

  const organizationId = useSelector(selectSelectedOrganizationId);
  const organizationName = useSelector(selectSelectedOrganizationName);

  useEffect(() => {
    if (isNil(organizationId) || !isNil(locations)) return;

    dispatch(setPageTitle(`${organizationName} - ${PAGE_TITLES.LOCATIONS}`));

    makeRequest(
      () => getOrganizationLocations(organizationId),
      { type: GET_ORGANIZATION_LOCATIONS_SUCCESS },
      ERROR_MESSAGES.GET_ORGANIZATION_LOCATIONS_FAILURE
    );
  }, [dispatch, locations, makeRequest, organizationId, organizationName]);

  useEffect(() => {
    return () => {
      dispatch(resetOrganizationLocations());
    };
  }, [dispatch]);

  const goToCreateLocation = useCallback(() => history.push(`${location.pathname}/${NEW_ITEM_ROUTE}`), [
    history,
    location,
  ]);

  return (
    <PageWrapper>
      <Box mb={2} width="100%">
        <SportScaleTable
          actions={[
            {
              icon: '',
              text: can(PERMISSIONS.EDIT_LOCATIONS) ? 'Edit' : 'View',
              onClick: rowData => history.push(`${location.pathname}/${rowData.id}`, { location: rowData }),
            },
          ]}
          columns={COLUMNS}
          components={{
            Action: SportScaleTableActionButton(classes.actionButton, false),
            Toolbar: SportScaleTableToolbar(
              can(PERMISSIONS.CREATE_LOCATIONS) ? [{ text: 'create location', onClick: goToCreateLocation }] : []
            ),
          }}
          data={locations || []}
          isLoading={isLoadingLocations}
          options={{ toolbar: true }}
        />
      </Box>
    </PageWrapper>
  );
};

const useStyles = makeStyles(theme => ({
  actionButton: {
    marginRight: theme.spacing(1),
  },
}));

LocationsContainer.propTypes = {};

LocationsContainer.defaultProps = {
  organization: null,
};

export default LocationsContainer;
